import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { getEventStatusDisplay, formatDate } from "../utils/eventHelpers";
import { STATUS_OPTIONS, EVENT_TYPES } from "../types/eventTypes";

const EventList = ({
  events = [],
  onSelect,
  onDelete,
  filters = {},
  onFilterChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange && onFilterChange({ search: searchTerm });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    onFilterChange &&
      onFilterChange({ search: "", status: "all", eventType: "all" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No events found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Create your first event to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search events, customers..."
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </form>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={filters?.eventType || "all"}
              onChange={(e) => onFilterChange({ eventType: e.target.value })}
              label="Event Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              {EVENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {events.map((event) => {
          const statusDisplay = getEventStatusDisplay(event.status);
          return (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                    cursor: "pointer",
                  },
                }}
                onClick={() => onSelect && onSelect(event.id)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="h6" noWrap>
                      {event.eventDetails?.eventName}
                    </Typography>
                    <Chip
                      label={statusDisplay.label}
                      color={statusDisplay.color}
                      size="small"
                    />
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {event.eventDetails?.eventType} • {event.eventCode}
                  </Typography>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {event.eventDetails?.customer?.customerName ||
                        "No client"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(event.eventDetails?.bookingDate)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total: ₹{event.invoice?.total || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        event.invoice?.balance > 0 ? "error" : "success.main"
                      }
                    >
                      Bal: ₹{event.invoice?.balance || 0}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={() => onSelect && onSelect(event.id)}
                        startIcon={<ViewIcon />}
                      >
                        View
                      </Button>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete && onDelete(event.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default EventList;
